'use client';

import RiPlayFill from '~icons/ri/play-fill';
import RiPauseFill from '~icons/ri/pause-fill';
import RiVolumeUpFill from '~icons/ri/volume-up-fill';
import RiVolumeMuteFill from '~icons/ri/volume-mute-fill';
import RiExternalLinkFill from '~icons/ri/external-link-fill';
import RiRefreshLine from '~icons/ri/refresh-line';
import RiLoader5Line from '~icons/ri/loader-5-line';
import RiSettings4Fill from '~icons/ri/settings-4-fill';
import RiChat4Fill from '~icons/ri/chat-4-fill';
import RiChat4Line from '~icons/ri/chat-4-line';
import RiEyeFill from '~icons/ri/eye-fill';
import RiPictureInPictureFill from '~icons/ri/picture-in-picture-fill';
import RiPictureInPictureLine from '~icons/ri/picture-in-picture-line';
import RiFullscreenLine from '~icons/ri/fullscreen-line';
import RiFullscreenExitLine from '~icons/ri/fullscreen-exit-line';

import React, { FC, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { akony } from '@/utils/fonts/fonts';

interface TwitchEmbedProps {
  onSelectedStreamer: Function;
  channel: string;
  isSelected: boolean;
  className?: string;
}

interface Quality {
  bitrate: number;
  codecs: string;
  framerate: number;
  group: string;
  height: number;
  isDefault: boolean;
  name: string;
  width: number;
}

interface PlaybackStats {
  backendVersion: string;
  bufferSize: number;
  codecs: string;
  displayResolution: string;
  fps: number;
  hlsLatencyBroadcaster: number;
  latencyMode: number;
  playbackRate: number;
  skippedFrames: number;
  videoResolution: string;
}

const TwitchEmbed: FC<TwitchEmbedProps> = (props) => {
  const [volume, setVolume] = useState<number>(0);
  const [togglePlaying, setPlaying] = useState<boolean>(false);
  const [toggleMute, setMute] = useState<boolean>(true);
  const [toggleChat, setChat] = useState<boolean>(false);
  const [viewersCount, setViewersCount] = useState<number>(0);
  const [isStreamOffline, setIsStreamOffline] = useState<boolean>(false);
  const [isPlayerLoading, setIsPlayerLoading] = useState<boolean>(true);
  const [playerInstance, setPlayerInstance] = useState<any>();
  const [playbackStats, setPlaybackStats] = useState<PlaybackStats>();
  const [qualityList, setQualityList] = useState<Quality[]>([]);
  const [selectedQuality, setSelectedQuality] = useState<string>();
  const [isQualityListShowed, setIsQualityListShowed] =
    useState<boolean>(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout>();
  const [intervalViewersCountId, setIntervalViewersCountId] =
    useState<NodeJS.Timeout>();

  const PLAYBACK_UPDATE_INTERVAL: number = 5;
  const VIEWERS_REFRESH_INTERVAL: number = 30;

  useEffect(() => {
    if (playerInstance == undefined) {
      setIsQualityListShowed(false);
      setPlaying(false);
      setMute(true);
      setVolume(0);

      // After player update remove other player for evade player elements stacking
      const parentElement = document.getElementById(
        `twitch-embed-${props.channel}`
      );
      if (parentElement) {
        parentElement.innerHTML = '';
      }

      const player = new Twitch.Player(`twitch-embed-${props.channel}`, {
        width: '100%',
        height: '100%',
        channel: props.channel,
        layout: 'video',
        autoplay: false,
        controls: false,
        parent: ['tijoe.net'],
      });

      setPlayerInstance(player);

      player.addEventListener(Twitch.Player.READY, () => {
        if (player) {
          setIntervalId(
            setInterval(() => {
              setPlaybackStats(player.getPlaybackStats());
            }, PLAYBACK_UPDATE_INTERVAL * 1000)
          );

          setIntervalViewersCountId(
            setInterval(async () => {
              const streamerInfo = await fetch(
                `https://api.twitch.tv/helix/streams?user_login=${props.channel}`,
                {
                  headers: {
                    'Client-ID': process.env.TWITCH_CLIENT_ID as string,
                    Authorization: `Bearer ${
                      process.env.TWITCH_ACCESS_TOKEN as string
                    }`,
                  },
                  cache: 'no-cache',
                }
              ).then((data: any) => {
                return data.json();
              });

              if (streamerInfo && streamerInfo.data[0]) {
                setViewersCount(streamerInfo.data[0].viewer_count);
                console.info(
                  `[${props.channel}][VIEWERS_COUNT]: ${streamerInfo.data[0].viewer_count}`
                );
              }
            }, VIEWERS_REFRESH_INTERVAL * 1000)
          );
        }

        setIsPlayerLoading(false);
        player.disableCaptions();
      });

      player.addEventListener(Twitch.Player.OFFLINE, () => {
        setIsStreamOffline(true);
      });

      player.addEventListener(Twitch.Player.ONLINE, () => {
        setIsStreamOffline(false);
      });

      player.addEventListener(Twitch.Player.PLAY, () => {
        console.log('PLAY event triggered');
        setPlaying(true);
      });

      player.addEventListener(Twitch.Player.PAUSE, () => {
        console.log('PAUSE event triggered');
        setPlaying(false);
      });

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
          clearInterval(intervalViewersCountId);
          setIntervalId(undefined);
        }
      };
    }
  }, [playerInstance]);

  useEffect(() => {
    if (playerInstance) {
      if (togglePlaying) {
        try {
          // Trying to get list of stream qualities
          setQualityList(playerInstance.getQualities());
          setSelectedQuality(playerInstance.getQuality());

          // Set quality to source after start playing
          playerInstance.setQuality(qualityList![1].name);
        } catch {
          console.error('Error while trying to set source quality');
        }

        playerInstance.play();
      } else {
        playerInstance.pause();
      }

      playerInstance.setMuted(toggleMute);
      playerInstance.setVolume(volume);
    }
  }, [volume, toggleMute, togglePlaying, playerInstance]);

  // Offline player
  if (isStreamOffline) {
    return (
      <section
        className={classNames(
          `relative grid rounded-xl overflow-hidden aspect-video bg-[#151515]/50 p-2 items-end select-none`
        )}
      >
        <Link
          href={`https://www.twitch.tv/${props.channel}`}
          target='_blank'
          className='absolute flex flex-row gap-1 items-center text-sm top-0 m-2 bg-[#151515]/50 px-4 py-1 rounded-full md:hover:bg-[#151515] transition-colors duration-200 ease-in-out'
        >
          {props.channel}
          <RiExternalLinkFill className='text-xs' />
        </Link>
        <div>
          <p
            style={akony.style}
            className='text-4xl md:text-5xl text-zinc-800/50 leading-none'
          >
            Stream
          </p>
          <p
            style={akony.style}
            className='text-4xl md:text-5xl text-rose-800/20 leading-10'
          >
            offline
          </p>
        </div>
      </section>
    );
  }

  // Player
  return (
    <>
      <section
        className={classNames(
          `group flex flex-col xl:flex-row justify-center relative rounded-xl items-center overflow-hidden ${props.className}`,
          {
            'cursor-default': isPlayerLoading,
            'cursor-pointer': !isPlayerLoading,
            'w-full h-full': toggleChat,
            'h-full aspect-video': !toggleChat && props.isSelected,
          }
        )}
      >
        {/* Twitch embed player */}
        <div className='relative grid grid-cols-1 xl:flex xl:flex-row h-full w-full'>
          {/* Loader */}
          {isPlayerLoading && (
            <section
              className={classNames(
                `grid bg-[#0b0b0b] w-full h-full select-none absolute items-center justify-center z-20`
              )}
            >
              <RiLoader5Line className='animate-spin text-4xl' />
            </section>
          )}

          <div className='relative h-full w-full xl:col-span-7'>
            <Link
              href={`https://www.twitch.tv/${props.channel}`}
              target='_blank'
              className='z-20 absolute flex flex-row gap-1 items-center text-sm top-0 m-2 bg-[#151515]/50 px-4 py-1 rounded-full md:hover:bg-[#151515] transition-colors duration-200 ease-in-out'
            >
              {props.channel}
              <RiExternalLinkFill className='text-xs' />
            </Link>

            <div
              className={classNames(
                'z-20 absolute flex flex-row gap-1 items-center text-sm bottom-0 m-2 bg-[#151515]/50 px-4 py-1 rounded-full md:hover:bg-[#151515] transition-colors duration-200 ease-in-out cursor-default',
                { hidden: !props.isSelected }
              )}
            >
              <RiEyeFill className='text-xs' />
              <p className='text-sm'>{viewersCount}</p>
            </div>

            {/* Custom controls for player */}
            <div className='z-10 group/range opacity-0 group-hover:opacity-100 absolute flex flex-row gap-3 left-[50%] translate-x-[-50%] mx-auto bottom-0 m-2 text-xl font-semibold transition-all duration-200 ease-in-out'>
              <div className='bg-[#151515] rounded-full px-4 py-2 flex flex-row items-center gap-3 text-lg'>
                <button
                  className='md:active:scale-150 transition-all duration-200 ease-in-out'
                  onClick={() => setPlaying(!togglePlaying)}
                >
                  {togglePlaying ? <RiPauseFill /> : <RiPlayFill />}
                </button>

                <button
                  className='md:active:scale-150 transition-all duration-200 ease-in-out'
                  onClick={() => {
                    setMute(!toggleMute); // Toggle mute state
                    setVolume(toggleMute ? 0.5 : 0); // Set volume based on the new mute state
                  }}
                >
                  {toggleMute ? <RiVolumeMuteFill /> : <RiVolumeUpFill />}
                </button>

                <input
                  type='range'
                  className={classNames('bg-white max-w-24', {
                    hidden: !props.isSelected,
                  })}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(e) => {
                    const eVol = Number(e.target.value);
                    setVolume(eVol);
                    eVol == 0 ? setMute(true) : setMute(false);
                  }}
                  value={volume}
                />

                <p
                  className={classNames('text-sm', {
                    hidden: !playbackStats,
                    block: playbackStats,
                  })}
                >
                  {playbackStats
                    ? Math.round(playbackStats.hlsLatencyBroadcaster * 10) / 10
                    : ''}
                  s
                </p>

                {props.isSelected ? (
                  <button onClick={() => props.onSelectedStreamer(undefined)}>
                    <RiFullscreenExitLine />
                  </button>
                ) : (
                  <button
                    onClick={() => props.onSelectedStreamer(props.channel)}
                  >
                    <RiFullscreenLine />
                  </button>
                )}

                <section
                  className={classNames('relative items-center flex', {
                    hidden: !props.isSelected,
                  })}
                >
                  <button
                    onClick={() => setIsQualityListShowed(!isQualityListShowed)}
                  >
                    <RiSettings4Fill />
                  </button>
                  <section
                    className={classNames(
                      'absolute bottom-8 left-[50%] translate-x-[-50%] flex flex-col bg-[#151515] rounded-md overflow-hidden',
                      {
                        block: isQualityListShowed,
                        hidden: !isQualityListShowed,
                      }
                    )}
                  >
                    {qualityList.length > 0 ? (
                      qualityList.map((quality, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            playerInstance.setQuality(quality.name);
                            setSelectedQuality(quality.name);
                          }}
                          className={classNames(
                            'text-sm px-5 py-2 md:hover:bg-zinc-800',
                            { 'bg-zinc-800': selectedQuality == quality.name }
                          )}
                        >
                          {quality.name == 'auto'
                            ? 'Авто'
                            : quality.name.includes('(source)')
                            ? quality.name.replace('(source)', '')
                            : quality.name}
                        </button>
                      ))
                    ) : (
                      <></>
                    )}
                  </section>
                </section>
                <button
                  className={classNames(
                    'md:active:scale-150 transition-all duration-200 ease-in-out',
                    { hidden: !props.isSelected }
                  )}
                  onClick={() => setPlayerInstance(undefined)}
                >
                  <RiRefreshLine />
                </button>
                <button
                  className={classNames(
                    'md:active:scale-150 transition-all duration-200 ease-in-out',
                    { hidden: !props.isSelected }
                  )}
                  onClick={() => setChat(!toggleChat)}
                >
                  {!toggleChat ? <RiChat4Fill /> : <RiChat4Line />}
                </button>
              </div>
            </div>
            {/* Twitch player */}
            <div
              id={`twitch-embed-${props.channel}`}
              className={classNames('aspect-video w-full h-full', {
                invisible: isPlayerLoading,
              })}
            />
          </div>

          {/* Twitch chat */}
          {isStreamOffline ? (
            <></>
          ) : !toggleChat ? (
            <></>
          ) : (
            <iframe
              className={classNames(
                'xl:col-span-3 h-[30rem] xl:h-full xl:w-1/2',
                { hidden: !toggleChat || !props.isSelected, block: toggleChat }
              )}
              src={`https://www.twitch.tv/embed/${props.channel}/chat?parent=tijoe.net&parent=localhost&parent=live.tijoe-homepage.pages.dev&darkpopout`}
              height='100%'
              width='100%'
              sandbox='allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-modals'
            ></iframe>
          )}
        </div>
      </section>
    </>
  );
};

export default TwitchEmbed;
